import {AuthModel} from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const IMPERSONATOR_SESSION_STORAGE_KEY = 'nosi-impersonator-auth-v1'
const IMPERSONATED_AUTH_SESSION_STORAGE_KEY = 'nosi-impersonated-auth-v1'
const TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY = 'nosi-tab-auth-override-v1'
const TAB_AUTH_LOGOUT_EPOCH_SESSION_STORAGE_KEY = 'nosi-tab-auth-logout-epoch-v1'
const PENDING_IMPERSONATION_REVOCATION_SESSION_STORAGE_KEY =
  'nosi-pending-impersonation-revocation-v1'
const AUTH_LOGOUT_EPOCH_LOCAL_STORAGE_KEY = 'nosi-auth-logout-epoch-v1'
let tabAuthInvalidated = false

const getLogoutEpoch = (): string | undefined => {
  if (typeof localStorage === 'undefined') return

  try {
    return localStorage.getItem(AUTH_LOGOUT_EPOCH_LOCAL_STORAGE_KEY) || ''
  } catch (error) {
    console.error('AUTH LOGOUT EPOCH READ ERROR', error)
  }
}

const clearTabAuthState = () => {
  tabAuthInvalidated = false
  if (typeof sessionStorage === 'undefined') return

  try {
    sessionStorage.removeItem(IMPERSONATOR_SESSION_STORAGE_KEY)
    sessionStorage.removeItem(IMPERSONATED_AUTH_SESSION_STORAGE_KEY)
    sessionStorage.removeItem(TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY)
    sessionStorage.removeItem(TAB_AUTH_LOGOUT_EPOCH_SESSION_STORAGE_KEY)
  } catch (error) {
    console.error('TAB AUTH SESSION STORAGE CLEAR ERROR', error)
  }
}

const prepareTabAuthState = (): boolean => {
  if (typeof sessionStorage === 'undefined') return false
  if (tabAuthInvalidated) return false

  const logoutEpoch = getLogoutEpoch()
  if (logoutEpoch === undefined) return false

  try {
    const storedEpoch = sessionStorage.getItem(TAB_AUTH_LOGOUT_EPOCH_SESSION_STORAGE_KEY)
    if (storedEpoch !== null && storedEpoch !== logoutEpoch) {
      // Keep the raw target token just long enough for the storage-event
      // handler to revoke it, but never allow it (or persistent auth) to be
      // selected for another API request in this tab.
      tabAuthInvalidated = true
      return false
    }

    if (storedEpoch === null) {
      sessionStorage.setItem(TAB_AUTH_LOGOUT_EPOCH_SESSION_STORAGE_KEY, logoutEpoch)
    }

    return true
  } catch (error) {
    console.error('TAB AUTH LOGOUT EPOCH SAVE ERROR', error)
    tabAuthInvalidated = true
    return false
  }
}

const readTabAuth = (key: string, errorLabel: string): AuthModel | undefined => {
  if (typeof sessionStorage === 'undefined' || !prepareTabAuthState()) return

  try {
    const storedValue = sessionStorage.getItem(key)
    if (!storedValue) return
    const auth = JSON.parse(storedValue) as AuthModel
    return auth?.api_token ? auth : undefined
  } catch (error) {
    console.error(errorLabel, error)
    tabAuthInvalidated = true
  }
}

const getImpersonatedAuth = (): AuthModel | undefined => {
  return readTabAuth(
    IMPERSONATED_AUTH_SESSION_STORAGE_KEY,
    'IMPERSONATED AUTH SESSION STORAGE READ ERROR'
  )
}

const getImpersonatedAuthSnapshot = (): AuthModel | undefined => {
  if (typeof sessionStorage === 'undefined') return

  try {
    const storedValue = sessionStorage.getItem(IMPERSONATED_AUTH_SESSION_STORAGE_KEY)
    if (!storedValue) return
    const auth = JSON.parse(storedValue) as AuthModel
    return auth?.api_token ? auth : undefined
  } catch (error) {
    console.error('IMPERSONATED AUTH SNAPSHOT READ ERROR', error)
  }
}

const getPersistentAuth = (): AuthModel | undefined => {
  if (typeof localStorage === 'undefined') {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const getTabAuthOverride = (): AuthModel | undefined => {
  return readTabAuth(
    TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY,
    'TAB AUTH OVERRIDE SESSION STORAGE READ ERROR'
  )
}

const getAuth = (): AuthModel | undefined => {
  const impersonatedAuth = getImpersonatedAuth()
  if (impersonatedAuth) return impersonatedAuth
  if (tabAuthInvalidated) return

  const tabAuthOverride = getTabAuthOverride()
  if (tabAuthOverride) return tabAuthOverride
  if (tabAuthInvalidated) return

  return getPersistentAuth()
}

const setAuth = (auth: AuthModel) => {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

const getImpersonatorAuth = (): AuthModel | undefined => {
  return readTabAuth(
    IMPERSONATOR_SESSION_STORAGE_KEY,
    'IMPERSONATOR AUTH SESSION STORAGE READ ERROR'
  )
}

const setImpersonatorAuth = (auth: AuthModel) => {
  if (typeof sessionStorage === 'undefined' || !prepareTabAuthState()) return false

  try {
    sessionStorage.removeItem(TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY)
    sessionStorage.setItem(IMPERSONATOR_SESSION_STORAGE_KEY, JSON.stringify(auth))
    return true
  } catch (error) {
    console.error('IMPERSONATOR AUTH SESSION STORAGE SAVE ERROR', error)
    clearTabAuthState()
    return false
  }
}

const removeImpersonatorAuth = () => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(IMPERSONATOR_SESSION_STORAGE_KEY)
  } catch (error) {
    console.error('IMPERSONATOR AUTH SESSION STORAGE REMOVE ERROR', error)
  }
}

const setImpersonatedAuth = (auth: AuthModel) => {
  if (typeof sessionStorage === 'undefined' || !prepareTabAuthState()) return false

  try {
    sessionStorage.setItem(IMPERSONATED_AUTH_SESSION_STORAGE_KEY, JSON.stringify(auth))
    return true
  } catch (error) {
    console.error('IMPERSONATED AUTH SESSION STORAGE SAVE ERROR', error)
    return false
  }
}

const seedImpersonationAuthForWindow = (
  targetWindow: Window,
  impersonatorAuth: AuthModel,
  impersonatedAuth: AuthModel
) => {
  const logoutEpoch = getLogoutEpoch()
  if (logoutEpoch === undefined) return false

  try {
    const targetSessionStorage = targetWindow.sessionStorage
    targetSessionStorage.removeItem(TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY)
    targetSessionStorage.setItem(IMPERSONATOR_SESSION_STORAGE_KEY, JSON.stringify(impersonatorAuth))
    targetSessionStorage.setItem(IMPERSONATED_AUTH_SESSION_STORAGE_KEY, JSON.stringify(impersonatedAuth))
    targetSessionStorage.setItem(TAB_AUTH_LOGOUT_EPOCH_SESSION_STORAGE_KEY, logoutEpoch)
    return true
  } catch (error) {
    console.error('NEW TAB IMPERSONATION AUTH SEED ERROR', error)
    return false
  }
}

const removeImpersonatedAuth = () => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(IMPERSONATED_AUTH_SESSION_STORAGE_KEY)
  } catch (error) {
    console.error('IMPERSONATED AUTH SESSION STORAGE REMOVE ERROR', error)
  }
}

const getPendingImpersonationRevocation = (): AuthModel | undefined => {
  if (typeof sessionStorage === 'undefined') return

  try {
    const storedValue = sessionStorage.getItem(
      PENDING_IMPERSONATION_REVOCATION_SESSION_STORAGE_KEY
    )
    if (!storedValue) return
    const auth = JSON.parse(storedValue) as AuthModel
    return auth?.api_token ? auth : undefined
  } catch (error) {
    console.error('PENDING IMPERSONATION REVOCATION READ ERROR', error)
  }
}

const setPendingImpersonationRevocation = (auth: AuthModel) => {
  if (typeof sessionStorage === 'undefined') return false

  try {
    sessionStorage.setItem(
      PENDING_IMPERSONATION_REVOCATION_SESSION_STORAGE_KEY,
      JSON.stringify(auth)
    )
    return true
  } catch (error) {
    console.error('PENDING IMPERSONATION REVOCATION SAVE ERROR', error)
    return false
  }
}

const removePendingImpersonationRevocation = () => {
  if (typeof sessionStorage === 'undefined') return

  try {
    sessionStorage.removeItem(PENDING_IMPERSONATION_REVOCATION_SESSION_STORAGE_KEY)
  } catch (error) {
    console.error('PENDING IMPERSONATION REVOCATION REMOVE ERROR', error)
  }
}

const setTabAuthOverride = (auth: AuthModel) => {
  if (typeof sessionStorage === 'undefined' || !prepareTabAuthState()) return false

  try {
    sessionStorage.setItem(TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY, JSON.stringify(auth))
    return true
  } catch (error) {
    console.error('TAB AUTH OVERRIDE SESSION STORAGE SAVE ERROR', error)
    return false
  }
}

const removeTabAuthOverride = () => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY)
  } catch (error) {
    console.error('TAB AUTH OVERRIDE SESSION STORAGE REMOVE ERROR', error)
  }
}

const publishLogout = () => {
  if (typeof localStorage === 'undefined') return false

  try {
    localStorage.setItem(
      AUTH_LOGOUT_EPOCH_LOCAL_STORAGE_KEY,
      `${Date.now()}-${Math.random().toString(36).slice(2)}`
    )
    return true
  } catch (error) {
    console.error('AUTH LOGOUT EPOCH SAVE ERROR', error)
    return false
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {headers?: {[key: string]: any}}) => {
      const headers = config.headers || (config.headers = {})
      const explicitAuthorization = headers.Authorization || headers.authorization
      if (explicitAuthorization) {
        return config
      }

      const auth = getAuth()
      if (auth && auth.api_token) {
        headers.Authorization = `Bearer ${auth.api_token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )
}

export {
  getAuth,
  getLogoutEpoch,
  getPersistentAuth,
  setAuth,
  removeAuth,
  getImpersonatedAuth,
  getImpersonatedAuthSnapshot,
  setImpersonatedAuth,
  seedImpersonationAuthForWindow,
  removeImpersonatedAuth,
  getPendingImpersonationRevocation,
  setPendingImpersonationRevocation,
  removePendingImpersonationRevocation,
  getImpersonatorAuth,
  setImpersonatorAuth,
  removeImpersonatorAuth,
  getTabAuthOverride,
  setTabAuthOverride,
  removeTabAuthOverride,
  clearTabAuthState,
  publishLogout,
  AUTH_LOCAL_STORAGE_KEY,
  IMPERSONATOR_SESSION_STORAGE_KEY,
  IMPERSONATED_AUTH_SESSION_STORAGE_KEY,
  TAB_AUTH_OVERRIDE_SESSION_STORAGE_KEY,
  AUTH_LOGOUT_EPOCH_LOCAL_STORAGE_KEY,
}
